import { useId } from "react";

import { ProvenanceEntryList } from "../provenance/ProvenanceEntryList.tsx";
import type { ProvenanceHandlers } from "../provenance/ProvenanceEntryList.tsx";
import { ProvenanceTimeline } from "../provenance/ProvenanceTimeline.tsx";
import { PROVENANCE_COPY } from "../provenance/provenance.constants.ts";
import { Spotlight } from "../spotlight/Spotlight.tsx";
import type { SpotlightView } from "../spotlight/useSpotlight.ts";
import type { EntryRow, TimelineRow } from "../provenance/provenance.model.ts";
import { REGISTER_COPY } from "./register.constants.ts";
import "../provenance/provenance.css";

export interface ProvenancePanelModel {
  rows: EntryRow[];
  timeline: TimelineRow[];
  openEntryId: string | null;
  refusals: { entryId: string; sentences: string[] } | null;
  /** True at the cap, which disables + Add Entry rather than silently ignoring it. */
  full: boolean;
}

/**
 * Step 3.
 *
 * A chain of title, one row per link, held in this browser until the wizard is
 * submitted — the same deferral the photographs use, and for the same reason:
 * `ondo.provenance_entries.work_id` is `not null`, and the work is not created
 * until the last step. An abandoned wizard leaves no entry, no document row and
 * nothing in the bucket.
 *
 * Exhibitions keep their textarea at the foot. The mock covers provenance only,
 * and there is no structured table behind an exhibition line — a form that
 * pretended otherwise would be collecting a shape the registry cannot store.
 */
export function ProvenancePanel({
  model,
  exhibitions,
  handlers,
  spotlight,
  onCloseDocument,
  onAdd,
  onSetExhibitions,
}: {
  model: ProvenancePanelModel;
  exhibitions: string;
  handlers: ProvenanceHandlers;
  spotlight: SpotlightView;
  onCloseDocument: () => void;
  onAdd: () => void;
  onSetExhibitions: (value: string) => void;
}) {
  const exhibitionField = useId();

  return (
    <div className="register__panel provenance">
      <div className="provenance__head">
        <h2 className="provenance__title">Provenance</h2>
        <button
          type="button"
          className="provenance__add"
          disabled={model.full}
          title={model.full ? PROVENANCE_COPY.tooManyEntries : undefined}
          onClick={onAdd}
        >
          {PROVENANCE_COPY.addEntry}
        </button>
      </div>

      <p className="provenance__help">{REGISTER_COPY.provenanceHelp}</p>
      <p className="provenance__callout">{REGISTER_COPY.provenanceOptional}</p>

      <ProvenanceEntryList
        rows={model.rows}
        openEntryId={model.openEntryId}
        refusals={model.refusals}
        handlers={handlers}
      />

      <ProvenanceTimeline rows={model.timeline} />

      <div className="register__field">
        <label htmlFor={exhibitionField}>Exhibition history</label>
        <textarea
          id={exhibitionField}
          rows={4}
          value={exhibitions}
          placeholder="Erwin Wurm: One Minute Sculptures, MUMOK, Vienna, 2006"
          onChange={(event) => onSetExhibitions(event.target.value)}
        />
        <p className="register__help">{REGISTER_COPY.exhibitionsHelp}</p>
      </div>

      {/* A draft's documents are on this device and have not been uploaded, so
          what opens here is the file the collector just picked. */}
      <Spotlight view={spotlight} onClose={onCloseDocument} />
    </div>
  );
}
